// Shared helpers for the chart pages.

// Web Mercator y, normalized to 0..1 (north pole 0, south pole 1).
function mercatorY(lat) {
  const clamped = Math.max(-85.05112878, Math.min(85.05112878, lat))
  const rad = (clamped * Math.PI) / 180
  return 0.5 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / (2 * Math.PI)
}

// What the map box actually measures, by breakpoint. The page puts the map in a
// Mantine `size="lg"` Container (1140px cap, 16px gutters) at `min(560px, 60vh)`
// tall, so the box is roughly a full desktop column, a narrower tablet column,
// or a phone's width at a shorter height.
//
// Assumed rather than measured because `initialView` has to exist before the map
// mounts — there is no container to measure yet. One 640x520 guess used to serve
// all three, which fit a phone and left a desktop chart floating in ~0.9 zoom of
// dead padding; the chart's frame was right, the map was just standing too far
// back from it. ExploreMap still corrects the last of it from the real container
// once the style loads (`initialBounds`); these sizes are what decides the frame
// that gets painted, and the tiles that get fetched, before that happens.
//
// Ordered widest first; the first match wins. 1024 is where the Container stops
// growing enough to matter, 768 is ExploreMap's own phone/desktop line.
//
// The phone bucket matches show/_chart.css's mobile-first pass (2026-08-28):
// full-bleed width (no gutters, 390 is the modal phone) at min(600px, 68svh)
// tall (~540 on an 844pt phone). Change the CSS, change this row.
const CONTAINERS = [
  { minWidth: 1024, width: 1140, height: 560 },
  { minWidth: 768, width: 640, height: 520 },
  { minWidth: 0, width: 390, height: 540 },
]

// The widest bucket on the server, where there is no window to ask: the map does
// not render during SSR (LazyMap draws a skeleton), so this value is never the
// one a visitor sees — it only has to be a sane constant.
export function assumedContainer(innerWidth) {
  const w =
    innerWidth ?? (typeof window === 'undefined' ? CONTAINERS[0].minWidth : window.innerWidth)
  return CONTAINERS.find((c) => w >= c.minWidth) ?? CONTAINERS[CONTAINERS.length - 1]
}

// A { latitude, longitude, zoom } that frames a chart's bounding box.
//
// The map is handed a view rather than only told to fitBounds because ExploreMap
// drives its camera from state on desktop: the view is what the first frame is
// painted at, and a fit is what refines it on load. The result is deliberately a
// little loose — MARGIN pulls the zoom back so an assumed container that is off
// by a breakpoint still shows the whole chart rather than cropping it.
//
// Longitudes past 180 are left alone. Seven Alaska/Pacific charts are drawn in
// that frame and maplibre wraps a center longitude itself, so converting here
// would only risk sending the camera to the wrong side of the world.
export function viewFromBbox(bbox, { width, height } = {}) {
  if (!bbox || bbox.length !== 4 || bbox.some((v) => v == null)) return null
  const [west, south, east, north] = bbox.map(Number)
  const assumed = assumedContainer()
  const boxWidth = width ?? assumed.width
  const boxHeight = height ?? assumed.height

  const lngSpan = Math.max(Math.abs(east - west), 1e-4)
  const latSpan = Math.max(Math.abs(mercatorY(north) - mercatorY(south)), 1e-6)

  // 512px tiles: the world is 512 * 2^zoom pixels wide.
  const zoomForWidth = Math.log2((boxWidth * 360) / (512 * lngSpan))
  const zoomForHeight = Math.log2(boxHeight / (512 * latSpan))
  const MARGIN = 0.4

  return {
    latitude: (south + north) / 2,
    longitude: (west + east) / 2,
    zoom: Math.max(1, Math.min(13, Math.min(zoomForWidth, zoomForHeight) - MARGIN)),
  }
}

// QA escape hatch: `?frame=union` on a chart page ignores the main-panel
// override and opens on the full extent, so an override can be eyeballed
// against the frame it replaced on the same URL. Deliberately client-side —
// the server always ships the chart's frame of record, so no query string
// produces a second server-rendered version of a public page.
export function unionFrameRequested(search) {
  const query = search ?? (typeof window === 'undefined' ? '' : window.location.search)
  return new URLSearchParams(query).get('frame') === 'union'
}

// The bbox the page frames: the chart's main panel where the catalog tagged one
// (`view_bbox`, the A2 override), and the union of all its panels otherwise.
// A sheet whose insets sit far from the main panel would otherwise open on water
// the printed chart never framed there.
export function frameBbox(chart, { search } = {}) {
  if (!chart) return null
  const override = chart.view_bbox
  if (!override || override.length !== 4 || unionFrameRequested(search)) return chart.bbox
  return override
}
